import React, { useState } from 'react'
import {Plus, Minus} from 'lucide-react'
import type { ProductV2Detail, Lang } from './types'

interface ProductSpecsAccordionV2Props {
  product: ProductV2Detail
  currentLang: Lang
}

interface AccordionRow {
  label: string
  value: string
}

interface AccordionGroup {
  key: string
  title: string
  rows: AccordionRow[]
}

/**
 * ProductSpecsAccordionV2
 *
 * Bottom-most accordion section, all collapsed by default
 * (Barista Map style — minimal dividers, plus/minus icons, label/value rows).
 *
 * Three groups: Specifications · Brewing Guide · Shipping & Care.
 */
const ProductSpecsAccordionV2: React.FC<ProductSpecsAccordionV2Props> = ({ product, currentLang }) => {
  const isZh = currentLang === 'zh'
  const [openKey, setOpenKey] = useState<string | null>(null)

  const specRows: AccordionRow[] = [
    {
      label: isZh ? '產地 / 產區' : 'Origin / Region',
      value: [product.origin, product.region].filter(Boolean).join(' / ') || '—',
    },
    { label: isZh ? '海拔' : 'Altitude', value: product.specifications?.altitude || '—' },
    { label: isZh ? '品種' : 'Variety', value: product.specifications?.variety || '—' },
    { label: isZh ? '處理法' : 'Processing', value: product.specifications?.processing || '—' },
    { label: isZh ? '烘焙度' : 'Roast Level', value: product.specifications?.roast || '—' },
    { label: isZh ? '重量' : 'Weight', value: product.specifications?.weight || '227g' },
    {
      label: isZh ? '包裝' : 'Packaging',
      value: product.specifications?.packaging || (isZh ? '單向透氣閥袋' : 'One-way valve bag'),
    },
  ]

  const brewingRows: AccordionRow[] = [
    { label: isZh ? '水溫' : 'Water Temp.', value: '90–92°C' },
    { label: isZh ? '粉水比' : 'Coffee : Water', value: '1 : 15' },
    { label: isZh ? '研磨度' : 'Grind', value: isZh ? '中細研磨' : 'Medium-fine' },
    {
      label: isZh ? '建議器具' : 'Suggested Tools',
      value: (product.brewingMethods && product.brewingMethods.length > 0)
        ? product.brewingMethods.join(' / ')
        : (isZh ? 'V60 / Kalita / Chemex' : 'V60 / Kalita / Chemex'),
    },
  ]

  const shippingRows: AccordionRow[] = [
    {
      label: isZh ? '烘焙日期' : 'Roast Date',
      value: isZh ? '出貨前 3–7 天現烘' : 'Roasted 3–7 days before shipping',
    },
    {
      label: isZh ? '保存期限' : 'Best Before',
      value: isZh ? '烘焙日後 12 個月內' : '12 months from roast date',
    },
    {
      label: isZh ? '建議飲用' : 'Best Enjoyed',
      value: isZh
        ? '養豆 7–14 天後風味最佳，開封後 4 週內飲畢'
        : 'Rest 7–14 days; finish within 4 weeks of opening',
    },
    {
      label: isZh ? '保存方式' : 'Storage',
      value: isZh
        ? '避光、密封、常溫；勿冷藏'
        : 'Cool, dark, airtight. Do not refrigerate.',
    },
  ]

  const groups: AccordionGroup[] = [
    { key: 'specs', title: isZh ? '規格資訊' : 'Specifications', rows: specRows },
    { key: 'brewing', title: isZh ? '沖煮建議' : 'Brewing Guide', rows: brewingRows },
    { key: 'shipping', title: isZh ? '出貨與保存' : 'Shipping & Care', rows: shippingRows },
  ]

  const toggle = (key: string) => setOpenKey(openKey === key ? null : key)

  return (
    <div className="mt-10 border-t border-black/10">
      {groups.map((group, idx) => {
        const isOpen = openKey === group.key
        return (
          <div
            key={group.key}
            className={idx === groups.length - 1 ? 'border-b border-black/10' : ''}
          >
            <button
              onClick={() => toggle(group.key)}
              className="flex w-full items-center justify-between border-t border-black/10 py-6 text-left first:border-t-0"
              aria-expanded={isOpen}
            >
              <span
                className="text-[15px] text-black"
                style={{
                  fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
                  letterSpacing: '0.02em',
                }}
              >
                {group.title}
              </span>
              {isOpen ? (
                <Minus size={16} strokeWidth={1.5} className="text-black/60" />
              ) : (
                <Plus size={16} strokeWidth={1.5} className="text-black/60" />
              )}
            </button>

            {isOpen && (
              <div className="pb-8">
                <dl className="divide-y divide-black/5">
                  {group.rows.map((row, i) => (
                    <div key={i} className="grid grid-cols-1 gap-1 py-3 md:grid-cols-[140px_1fr] md:gap-6">
                      <dt
                        className="text-[12px] uppercase text-black/50"
                        style={{ letterSpacing: '0.18em' }}
                      >
                        {row.label}
                      </dt>
                      <dd
                        className="text-sm text-black/80"
                        style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif" }}
                      >
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ProductSpecsAccordionV2
